import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { 
  getRoles, createRole, updateRole, deleteRole, getUsers,
  getBranding, updateBranding, getRecurringEvents, createRecurringEvent,
  deleteRecurringEvent, generateRecurringEvents, getEventTypes, createEventType,
  deleteEventType, createNotification, getGroups, getSystemSettings, updateSystemSettings,
  getModules, getAllDynamicFields, createDynamicField, updateDynamicField, deleteDynamicField,
  getHomepageSettings, updateHomepageSettings
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Plus, Settings, Shield, Edit, Trash2, Users, Palette, Bell,
  Repeat, Calendar, Play, Coins, Globe, Clock, FileText,
  Package, Wallet, ClipboardList, Megaphone, Save, RefreshCw,
  Activity, Database, Check, X as XIcon, Home
} from 'lucide-react';

const ALL_PERMISSIONS = []; // Legacy - no longer used

const DYNAMIC_FIELD_TYPES = [
  { id: 'texto_corto', name: 'Texto corto' },
  { id: 'texto_largo', name: 'Texto largo' },
  { id: 'numero', name: 'Número' },
  { id: 'fecha', name: 'Fecha' },
  { id: 'booleano', name: 'Sí/No' },
  { id: 'seleccion_unica', name: 'Selección única' },
  { id: 'seleccion_multiple', name: 'Selección múltiple' },
  { id: 'email', name: 'Email' },
  { id: 'telefono', name: 'Teléfono' },
  { id: 'url', name: 'URL' },
  { id: 'moneda', name: 'Moneda' },
];

const CURRENCY_OPTIONS = [
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'USD', symbol: '$', name: 'Dólar estadounidense' },
  { code: 'BOB', symbol: 'Bs.', name: 'Boliviano' },
  { code: 'MXN', symbol: '$', name: 'Peso mexicano' },
  { code: 'ARS', symbol: '$', name: 'Peso argentino' },
  { code: 'COP', symbol: '$', name: 'Peso colombiano' },
  { code: 'PEN', symbol: 'S/', name: 'Sol peruano' },
  { code: 'CLP', symbol: '$', name: 'Peso chileno' },
  { code: 'GBP', symbol: '£', name: 'Libra esterlina' },
];

const TIMEZONE_OPTIONS = [
  { id: 'Europe/Madrid', name: 'España (Madrid)' },
  { id: 'Europe/London', name: 'Reino Unido (Londres)' },
  { id: 'America/La_Paz', name: 'Bolivia (La Paz)' },
  { id: 'America/Mexico_City', name: 'México (Ciudad de México)' },
  { id: 'America/Buenos_Aires', name: 'Argentina (Buenos Aires)' },
  { id: 'America/Bogota', name: 'Colombia (Bogotá)' },
  { id: 'America/Lima', name: 'Perú (Lima)' },
  { id: 'America/Santiago', name: 'Chile (Santiago)' },
];

export default function Admin() {
  const { hasPermission, isSuperadmin } = useAuth();
  const { refreshBranding } = useNotifications();
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [recurringEvents, setRecurringEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [modules, setModules] = useState([]);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Branding state
  const [branding, setBranding] = useState({
    organization_name: 'PICHIROCES AMAZONICOS',
    tagline: 'Fraternidad de Bailes Típicos del Beni',
    primary_color: '#064E3B',
    secondary_color: '#0284C7',
    accent_color: '#F59E0B',
    logo_url: '',
    favicon_url: ''
  });
  
  // System settings state
  const [systemSettings, setSystemSettings] = useState({
    currency_code: 'EUR',
    currency_symbol: '€',
    currency_position: 'before',
    decimal_separator: ',',
    thousands_separator: '.',
    decimal_places: 2,
    date_format: 'dd/MM/yyyy',
    time_format: 'HH:mm',
    timezone: 'Europe/Madrid',
    week_start: 1,
    language: 'es',
    attendance_statuses: [],
    inventory_categories: [],
    inventory_conditions: [],
    document_categories: [],
    finance_record_types: [],
    budget_categories: [],
    announcement_priorities: [],
    survey_question_types: [],
    notification_types: [],
    days_of_week: []
  });
  
  // Dialog states
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [showCreateRecurring, setShowCreateRecurring] = useState(false);
  const [showCreateNotification, setShowCreateNotification] = useState(false);
  const [showCreateEventType, setShowCreateEventType] = useState(false);
  const [showEditCategory, setShowEditCategory] = useState(null);
  const [editCategoryType, setEditCategoryType] = useState('');
  const [editCategoryData, setEditCategoryData] = useState({ id: '', name: '', color: '', icon: '' });

  // Form states
  const [roleForm, setRoleForm] = useState({ nombre: '', descripcion: '', es_directiva: false, nivel_acceso: 1, permisos: {}, hereda_de: '' });
  const [dynamicFieldForm, setDynamicFieldForm] = useState({
    modulo: '', nombre_campo: '', etiqueta: '', tipo: 'texto_corto',
    opciones: [], requerido: false, visible_en_listado: false, filtrable: false, orden: 0
  });
  const [showCreateDynamicField, setShowCreateDynamicField] = useState(false);
  // Homepage settings state
  const [homepageSettings, setHomepageSettings] = useState({
    descripcion: '', anio_fundacion: 2020,
    stats: { anios: 5, integrantes: 30, actuaciones: 50, paises: 3 },
    contacto: { email: '', whatsapp: '', ubicacion: 'Torrevieja, Valencia, España' },
    redes_sociales: { instagram: '', facebook: '', tiktok: '', youtube: '' },
    tagline_footer: 'Del corazón del Beni al mundo entero'
  });
  const [recurringForm, setRecurringForm] = useState({
    title: '', description: '', event_type: 'ensayo', location: '',
    target_roles: [], target_groups: [], day_of_week: 0,
    start_time: '19:00', end_time: '21:00', is_active: true
  });
  const [notificationForm, setNotificationForm] = useState({
    title: '', message: '', notification_type: 'general',
    target_users: [], target_roles: []
  });
  const [eventTypeForm, setEventTypeForm] = useState({
    name: '', color: '#0284C7', icon: 'calendar',
    requires_attendance: true, is_recurring: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rolesRes, usersRes, groupsRes, brandingRes, recurringRes, typesRes, settingsRes, modulesRes, dfRes, hpRes] = await Promise.all([
        getRoles(),
        getUsers(),
        getGroups(),
        getBranding(),
        getRecurringEvents(),
        getEventTypes(),
        getSystemSettings(),
        getModules(),
        getAllDynamicFields().catch(() => ({ data: [] })),
        getHomepageSettings().catch(() => ({ data: {} }))
      ]);
      setRoles(rolesRes.data);
      setUsers(usersRes.data);
      setGroups(groupsRes.data);
      setBranding(brandingRes.data);
      setRecurringEvents(recurringRes.data);
      setEventTypes(typesRes.data);
      setSystemSettings(settingsRes.data);
      setModules(modulesRes.data);
      setDynamicFields(dfRes.data);
      if (hpRes.data && Object.keys(hpRes.data).length > 0) {
        setHomepageSettings(prev => ({ ...prev, ...hpRes.data }));
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Role handlers
  const handleCreateRole = async () => {
    if (!roleForm.nombre) return toast.error('El nombre es requerido');
    try {
      await createRole(roleForm);
      toast.success('Rol creado');
      setShowCreateRole(false);
      setRoleForm({ nombre: '', descripcion: '', es_directiva: false, nivel_acceso: 1, permisos: {}, hereda_de: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear rol');
    }
  };

  const handleUpdateRole = async () => {
    try {
      await updateRole(editRole.id, roleForm);
      toast.success('Rol actualizado');
      setEditRole(null);
      setRoleForm({ nombre: '', descripcion: '', es_directiva: false, nivel_acceso: 1, permisos: {}, hereda_de: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar rol');
    }
  };

  const handleDeleteRole = async (id) => {
    if (!window.confirm('¿Desactivar este rol?')) return;
    try {
      await deleteRole(id);
      toast.success('Rol desactivado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desactivar rol');
    }
  };

  const handleEditRole = (role) => {
    setEditRole(role);
    setRoleForm({
      nombre: role.nombre || '',
      descripcion: role.descripcion || '',
      es_directiva: role.es_directiva || false,
      nivel_acceso: role.nivel_acceso || 1,
      permisos: role.permisos || {},
      hereda_de: role.hereda_de || ''
    });
  };

  const toggleModulePermission = (moduloId, accion) => {
    const newPermisos = { ...roleForm.permisos };
    if (!newPermisos[moduloId]) {
      newPermisos[moduloId] = {};
    }
    newPermisos[moduloId][accion] = !newPermisos[moduloId]?.[accion];
    setRoleForm({ ...roleForm, permisos: newPermisos });
  };

  const toggleAllModulePerms = (moduloId, actions) => {
    const newPermisos = { ...roleForm.permisos };
    const allOn = actions.every(a => newPermisos[moduloId]?.[a]);
    if (allOn) {
      delete newPermisos[moduloId];
    } else {
      newPermisos[moduloId] = {};
      actions.forEach(a => { newPermisos[moduloId][a] = true; });
    }
    setRoleForm({ ...roleForm, permisos: newPermisos });
  };

  // Dynamic field handlers
  const handleCreateDynamicField = async () => {
    if (!dynamicFieldForm.modulo || !dynamicFieldForm.nombre_campo || !dynamicFieldForm.etiqueta) {
      return toast.error('Módulo, nombre y etiqueta son requeridos');
    }
    try {
      await createDynamicField(dynamicFieldForm);
      toast.success('Campo dinámico creado');
      setShowCreateDynamicField(false);
      setDynamicFieldForm({
        modulo: '', nombre_campo: '', etiqueta: '', tipo: 'texto_corto',
        opciones: [], requerido: false, visible_en_listado: false, filtrable: false, orden: 0
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear campo');
    }
  };

  const handleDeleteDynamicField = async (id) => {
    if (!window.confirm('¿Desactivar este campo?')) return;
    try {
      await deleteDynamicField(id);
      toast.success('Campo desactivado');
      fetchData();
    } catch (error) {
      toast.error('Error al desactivar campo');
    }
  };

  // Save all settings
  const handleSaveBranding = async () => {
    setSaving(true);
    try {
      await updateBranding(branding);
      toast.success('Branding actualizado');
      refreshBranding();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSystemSettings = async () => {
    setSaving(true);
    try {
      await updateSystemSettings(systemSettings);
      toast.success('Configuración del sistema actualizada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Currency change handler
  const handleCurrencyChange = (code) => {
    const currency = CURRENCY_OPTIONS.find(c => c.code === code);
    if (currency) {
      setSystemSettings({
        ...systemSettings,
        currency_code: currency.code,
        currency_symbol: currency.symbol
      });
    }
  };

  // Category management
  const handleAddCategory = (type) => {
    setEditCategoryType(type);
    setEditCategoryData({ id: '', name: '', color: '#0284c7', icon: '' });
    setShowEditCategory('new');
  };

  const handleEditCategory = (type, item) => {
    setEditCategoryType(type);
    setEditCategoryData({ ...item });
    setShowEditCategory('edit');
  };

  const handleSaveCategory = () => {
    const key = editCategoryType;
    const newItem = { ...editCategoryData };
    if (!newItem.id) {
      newItem.id = newItem.name.toLowerCase().replace(/\s+/g, '_');
    }
    
    let currentList = [...(systemSettings[key] || [])];
    
    if (showEditCategory === 'new') {
      currentList.push(newItem);
    } else {
      const index = currentList.findIndex(c => c.id === editCategoryData.id);
      if (index >= 0) {
        currentList[index] = newItem;
      }
    }
    
    setSystemSettings({ ...systemSettings, [key]: currentList });
    setShowEditCategory(null);
    toast.success('Categoría guardada. Recuerda guardar los cambios del sistema.');
  };

  const handleDeleteCategory = (type, id) => {
    if (!window.confirm('¿Eliminar esta categoría?')) return;
    const currentList = systemSettings[type].filter(c => c.id !== id);
    setSystemSettings({ ...systemSettings, [type]: currentList });
    toast.success('Categoría eliminada. Recuerda guardar los cambios.');
  };

  // Recurring events handlers
  const handleCreateRecurring = async () => {
    if (!recurringForm.title) return toast.error('El título es requerido');
    try {
      await createRecurringEvent(recurringForm);
      toast.success('Evento recurrente creado');
      setShowCreateRecurring(false);
      setRecurringForm({
        title: '', description: '', event_type: 'ensayo', location: '',
        target_roles: [], target_groups: [], day_of_week: 0,
        start_time: '19:00', end_time: '21:00', is_active: true
      });
      fetchData();
    } catch (error) {
      toast.error('Error al crear');
    }
  };

  const handleGenerateEvents = async (id) => {
    try {
      const result = await generateRecurringEvents(id, 4);
      toast.success(result.data.message);
    } catch (error) {
      toast.error('Error al generar eventos');
    }
  };

  const handleDeleteRecurring = async (id) => {
    if (!window.confirm('¿Eliminar este evento recurrente?')) return;
    try {
      await deleteRecurringEvent(id);
      toast.success('Eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Notification handlers
  const handleSendNotification = async () => {
    if (!notificationForm.title || !notificationForm.message) return toast.error('Completa los campos');
    try {
      await createNotification(notificationForm);
      toast.success('Notificación enviada');
      setShowCreateNotification(false);
      setNotificationForm({ title: '', message: '', notification_type: 'general', target_users: [], target_roles: [] });
    } catch (error) {
      toast.error('Error al enviar');
    }
  };

  // Event type handlers
  const handleCreateEventType = async () => {
    if (!eventTypeForm.name) return toast.error('El nombre es requerido');
    try {
      await createEventType(eventTypeForm);
      toast.success('Tipo de evento creado');
      setShowCreateEventType(false);
      setEventTypeForm({ name: '', color: '#0284C7', icon: 'calendar', requires_attendance: true, is_recurring: false });
      fetchData();
    } catch (error) {
      toast.error('Error al crear');
    }
  };

  const handleDeleteEventType = async (id) => {
    if (!window.confirm('¿Eliminar este tipo de evento?')) return;
    try {
      await deleteEventType(id);
      toast.success('Eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getUserCountByRole = (roleId) => users.filter(u => u.role_id === roleId).length;

  // Render category list component
  const renderCategoryList = (type, title, showColor = true, showIcon = false) => {
    const items = systemSettings[type] || [];
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-base font-medium">{title}</Label>
          <Button variant="outline" size="sm" onClick={() => handleAddCategory(type)}>
            <Plus className="w-4 h-4 mr-1" />
            Añadir
          </Button>
        </div>
        <div className="border rounded-lg divide-y">
          {items.map((item) => (
            <div key={item.id} className="flex items-center justify-between p-3">
              <div className="flex items-center gap-3">
                {showColor && (
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: item.color || '#78716c' }}
                  />
                )}
                <span>{item.name}</span>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditCategory(type, item)}>
                  <Edit className="w-3 h-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => handleDeleteCategory(type, item.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <p className="p-4 text-center text-muted-foreground text-sm">No hay elementos</p>
          )}
        </div>
      </div>
    );
  };

  if (!hasPermission('administracion', 'ver')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-12 h-12 mx-auto mb-4 text-stone-300" />
          <p className="text-muted-foreground">No tienes acceso a esta sección</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="admin-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-emerald-900 font-['Outfit']">Panel de Administración</h1>
        <p className="text-muted-foreground">Control total del sistema y configuración avanzada</p>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="general" className="gap-2"><Settings className="w-4 h-4" />General</TabsTrigger>
          <TabsTrigger value="branding" className="gap-2"><Palette className="w-4 h-4" />Branding</TabsTrigger>
          <TabsTrigger value="currency" className="gap-2"><Coins className="w-4 h-4" />Moneda</TabsTrigger>
          <TabsTrigger value="categories" className="gap-2"><Package className="w-4 h-4" />Categorías</TabsTrigger>
          <TabsTrigger value="roles" className="gap-2"><Shield className="w-4 h-4" />Roles</TabsTrigger>
          <TabsTrigger value="recurring" className="gap-2"><Repeat className="w-4 h-4" />Periódicos</TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2"><Bell className="w-4 h-4" />Notificaciones</TabsTrigger>
          <TabsTrigger value="dynamic-fields" className="gap-2"><Database className="w-4 h-4" />Campos Dinámicos</TabsTrigger>
          <TabsTrigger value="homepage" className="gap-2"><Home className="w-4 h-4" />Homepage</TabsTrigger>
        </TabsList>

        {/* General Settings Tab */}
        <TabsContent value="general">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Configuración Regional
                </CardTitle>
                <CardDescription>Idioma, zona horaria y formato de fechas</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Zona Horaria</Label>
                  <Select 
                    value={systemSettings.timezone} 
                    onValueChange={(v) => setSystemSettings({...systemSettings, timezone: v})}
                  >
                    <SelectTrigger data-testid="timezone-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <SelectItem key={tz.id} value={tz.id}>{tz.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Formato de Fecha</Label>
                    <Select 
                      value={systemSettings.date_format} 
                      onValueChange={(v) => setSystemSettings({...systemSettings, date_format: v})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dd/MM/yyyy">dd/MM/yyyy (31/12/2024)</SelectItem>
                        <SelectItem value="MM/dd/yyyy">MM/dd/yyyy (12/31/2024)</SelectItem>
                        <SelectItem value="yyyy-MM-dd">yyyy-MM-dd (2024-12-31)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Formato de Hora</Label>
                    <Select 
                      value={systemSettings.time_format} 
                      onValueChange={(v) => setSystemSettings({...systemSettings, time_format: v})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="HH:mm">24 horas (14:30)</SelectItem>
                        <SelectItem value="hh:mm a">12 horas (02:30 PM)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Primer día de la semana</Label>
                  <Select 
                    value={String(systemSettings.week_start)} 
                    onValueChange={(v) => setSystemSettings({...systemSettings, week_start: parseInt(v)})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Lunes</SelectItem>
                      <SelectItem value="0">Domingo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleSaveSystemSettings} disabled={saving} className="w-full">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Guardando...' : 'Guardar Configuración Regional'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Resumen del Sistema
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between p-3 bg-stone-50 rounded-lg">
                  <span>Total de usuarios</span>
                  <span className="font-bold">{users.length}</span>
                </div>
                <div className="flex justify-between p-3 bg-stone-50 rounded-lg">
                  <span>Total de grupos</span>
                  <span className="font-bold">{groups.length}</span>
                </div>
                <div className="flex justify-between p-3 bg-stone-50 rounded-lg">
                  <span>Roles configurados</span>
                  <span className="font-bold">{roles.length}</span>
                </div>
                <div className="flex justify-between p-3 bg-stone-50 rounded-lg">
                  <span>Eventos periódicos</span>
                  <span className="font-bold">{recurringEvents.length}</span>
                </div>
                <div className="flex justify-between p-3 bg-stone-50 rounded-lg">
                  <span>Tipos de evento</span>
                  <span className="font-bold">{eventTypes.length}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Personalización de Marca
              </CardTitle>
              <CardDescription>Configura la identidad visual de tu organización</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre de la Organización</Label>
                  <Input
                    value={branding.organization_name}
                    onChange={(e) => setBranding({ ...branding, organization_name: e.target.value })}
                    data-testid="branding-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Eslogan / Subtítulo</Label>
                  <Input
                    value={branding.tagline}
                    onChange={(e) => setBranding({ ...branding, tagline: e.target.value })}
                    data-testid="branding-tagline"
                  />
                </div>
              </div>
              
              <Separator />
              
              <div>
                <Label className="text-base font-medium mb-4 block">Colores de la Marca</Label>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Color Primario</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={branding.primary_color}
                        onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                        className="w-14 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={branding.primary_color}
                        onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Color Secundario</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={branding.secondary_color}
                        onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                        className="w-14 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={branding.secondary_color}
                        onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Color de Acento</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={branding.accent_color}
                        onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                        className="w-14 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={branding.accent_color}
                        onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <Separator />
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>URL del Logo</Label>
                  <Input
                    value={branding.logo_url || ''}
                    onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL del Favicon</Label>
                  <Input
                    value={branding.favicon_url || ''}
                    onChange={(e) => setBranding({ ...branding, favicon_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>
              
              <Button onClick={handleSaveBranding} disabled={saving} className="w-full" data-testid="save-branding-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Guardando...' : 'Guardar Branding'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Currency Tab */}
        <TabsContent value="currency">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Coins className="w-5 h-5" />
                Configuración de Moneda
              </CardTitle>
              <CardDescription>Define la moneda y formato para todas las operaciones financieras</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Moneda</Label>
                  <Select 
                    value={systemSettings.currency_code} 
                    onValueChange={handleCurrencyChange}
                  >
                    <SelectTrigger data-testid="currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_OPTIONS.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.symbol} - {c.name} ({c.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Símbolo Personalizado</Label>
                  <Input
                    value={systemSettings.currency_symbol}
                    onChange={(e) => setSystemSettings({...systemSettings, currency_symbol: e.target.value})}
                    placeholder="€"
                    data-testid="currency-symbol"
                  />
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Posición del Símbolo</Label>
                  <Select 
                    value={systemSettings.currency_position} 
                    onValueChange={(v) => setSystemSettings({...systemSettings, currency_position: v})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="before">Antes del número (€100)</SelectItem>
                      <SelectItem value="after">Después del número (100€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Decimales</Label>
                  <Select 
                    value={String(systemSettings.decimal_places)} 
                    onValueChange={(v) => setSystemSettings({...systemSettings, decimal_places: parseInt(v)})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Sin decimales (100)</SelectItem>
                      <SelectItem value="2">2 decimales (100,00)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Separador Decimal</Label>
                  <Select 
                    value={systemSettings.decimal_separator} 
                    onValueChange={(v) => setSystemSettings({...systemSettings, decimal_separator: v})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value=",">Coma (100,00)</SelectItem>
                      <SelectItem value=".">Punto (100.00)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Separador de Miles</Label>
                  <Select 
                    value={systemSettings.thousands_separator} 
                    onValueChange={(v) => setSystemSettings({...systemSettings, thousands_separator: v})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value=".">Punto (1.000)</SelectItem>
                      <SelectItem value=",">Coma (1,000)</SelectItem>
                      <SelectItem value=" ">Espacio (1 000)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Preview */}
              <div className="p-4 bg-stone-50 rounded-lg">
                <Label className="text-sm text-muted-foreground">Vista previa</Label>
                <p className="text-2xl font-bold mt-2">
                  {systemSettings.currency_position === 'before' 
                    ? `${systemSettings.currency_symbol}1${systemSettings.thousands_separator}234${systemSettings.decimal_separator}${systemSettings.decimal_places > 0 ? '56' : ''}`
                    : `1${systemSettings.thousands_separator}234${systemSettings.decimal_separator}${systemSettings.decimal_places > 0 ? '56' : ''}${systemSettings.currency_symbol}`
                  }
                </p>
              </div>
              
              <Button onClick={handleSaveSystemSettings} disabled={saving} className="w-full">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Guardando...' : 'Guardar Configuración de Moneda'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Inventario
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {renderCategoryList('inventory_categories', 'Categorías de Inventario', false)}
                <Separator />
                {renderCategoryList('inventory_conditions', 'Estados de Inventario')}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Documentos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renderCategoryList('document_categories', 'Categorías de Documentos')}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Finanzas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {renderCategoryList('finance_record_types', 'Tipos de Movimiento')}
                <Separator />
                {renderCategoryList('budget_categories', 'Categorías de Presupuesto', false)}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5" />
                  Otros
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {renderCategoryList('attendance_statuses', 'Estados de Asistencia')}
                <Separator />
                {renderCategoryList('announcement_priorities', 'Prioridades de Anuncios')}
              </CardContent>
            </Card>
          </div>
          
          <Card className="mt-6">
            <CardContent className="pt-6">
              <Button onClick={handleSaveSystemSettings} disabled={saving} className="w-full">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Guardando...' : 'Guardar Todas las Categorías'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roles Tab - Granular Permissions Matrix */}
        <TabsContent value="roles">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Roles y Permisos Granulares
                </CardTitle>
                <CardDescription>Gestiona los roles y la matriz de permisos Módulo/Acción</CardDescription>
              </div>
              <Button onClick={() => { setShowCreateRole(true); setRoleForm({ nombre: '', descripcion: '', es_directiva: false, nivel_acceso: 1, permisos: {}, hereda_de: '' }); }} data-testid="create-role-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Rol
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {roles.filter(r => r.activo !== false).map((role) => (
                  <div key={role.id} className="border rounded-lg p-4" data-testid={`role-card-${role.nombre}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-stone-800">{role.nombre}</h4>
                            {role.nombre === 'Superadmin' && <Badge className="bg-red-100 text-red-700">Bypass Total</Badge>}
                            {role.es_directiva && <Badge variant="outline">Directiva</Badge>}
                            <Badge variant="secondary">Nivel {role.nivel_acceso}</Badge>
                          </div>
                          <p className="text-xs text-stone-500 mt-0.5">{role.descripcion}</p>
                          {role.hereda_de && (
                            <p className="text-xs text-blue-600 mt-0.5">
                              Hereda de: {roles.find(r => r.id === role.hereda_de)?.nombre || '?'}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{getUserCountByRole(role.id)} usuarios</Badge>
                        {role.nombre !== 'Superadmin' && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => handleEditRole(role)} data-testid={`edit-role-${role.nombre}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-red-600" onClick={() => handleDeleteRole(role.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    {/* Mini permission matrix for this role */}
                    {role.nombre !== 'Superadmin' && (
                      <div className="overflow-x-auto">
                        <div className="flex flex-wrap gap-1 mt-2">
                          {modules.map(mod => {
                            const modPerms = role.permisos?.[mod.id] || {};
                            const hasAny = mod.acciones.some(a => modPerms[a]);
                            if (!hasAny) return null;
                            return (
                              <Badge key={mod.id} variant="outline" className="text-xs">
                                {mod.nombre}: {mod.acciones.filter(a => modPerms[a]).join(', ')}
                              </Badge>
                            );
                          })}
                          {Object.keys(role.permisos || {}).length === 0 && !role.hereda_de && (
                            <span className="text-xs text-stone-400">Sin permisos configurados</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recurring Events Tab */}
        <TabsContent value="recurring">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Repeat className="w-5 h-5" />
                  Eventos Periódicos
                </CardTitle>
                <CardDescription>Configura ensayos y reuniones recurrentes</CardDescription>
              </div>
              <Button onClick={() => setShowCreateRecurring(true)} data-testid="create-recurring-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Periódico
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recurringEvents.map((event) => (
                  <div key={event.id} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium">{event.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {systemSettings.days_of_week?.find(d => d.id === event.day_of_week)?.name || `Día ${event.day_of_week}`} • {event.start_time}
                          {event.end_time && ` - ${event.end_time}`}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline">{event.event_type}</Badge>
                          <Badge variant={event.is_active ? 'default' : 'secondary'}>
                            {event.is_active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleGenerateEvents(event.id)}>
                          <Play className="w-4 h-4 mr-1" />
                          Generar 4 semanas
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-600" onClick={() => handleDeleteRecurring(event.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {recurringEvents.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No hay eventos periódicos configurados</p>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Event Types Section */}
          <Card className="mt-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Tipos de Evento
                </CardTitle>
                <CardDescription>Define los tipos de eventos disponibles</CardDescription>
              </div>
              <Button onClick={() => setShowCreateEventType(true)} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Tipo
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {eventTypes.map((type) => (
                  <div key={type.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: type.color + '20', color: type.color }}
                        >
                          <Calendar className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="font-medium">{type.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {type.requires_attendance ? 'Con asistencia' : 'Sin asistencia'}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="text-red-600" onClick={() => handleDeleteEventType(type.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Enviar Notificación Manual
              </CardTitle>
              <CardDescription>Envía notificaciones personalizadas a los miembros</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Título</Label>
                <Input
                  value={notificationForm.title}
                  onChange={(e) => setNotificationForm({...notificationForm, title: e.target.value})}
                  placeholder="Título de la notificación"
                  data-testid="notification-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Mensaje</Label>
                <Textarea
                  value={notificationForm.message}
                  onChange={(e) => setNotificationForm({...notificationForm, message: e.target.value})}
                  placeholder="Escribe el mensaje..."
                  rows={4}
                  data-testid="notification-message"
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select 
                  value={notificationForm.notification_type} 
                  onValueChange={(v) => setNotificationForm({...notificationForm, notification_type: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(systemSettings.notification_types || []).map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleSendNotification} className="w-full" data-testid="send-notification-btn">
                <Bell className="w-4 h-4 mr-2" />
                Enviar Notificación
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dynamic Fields Tab */}
        <TabsContent value="dynamic-fields">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Campos Dinámicos
                </CardTitle>
                <CardDescription>Campos personalizados para cualquier módulo</CardDescription>
              </div>
              <Button onClick={() => setShowCreateDynamicField(true)} data-testid="create-dynamic-field-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Campo
              </Button>
            </CardHeader>
            <CardContent>
              {modules.map(mod => {
                const modFields = dynamicFields.filter(f => f.modulo === mod.id);
                if (modFields.length === 0) return null;
                return (
                  <div key={mod.id} className="mb-6">
                    <h4 className="font-medium text-stone-700 mb-2">{mod.nombre}</h4>
                    <div className="space-y-2">
                      {modFields.map(field => (
                        <div key={field.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`dynamic-field-${field.nombre_campo}`}>
                          <div>
                            <span className="font-medium">{field.etiqueta}</span>
                            <span className="text-xs text-stone-500 ml-2">({field.nombre_campo})</span>
                            <div className="flex gap-1 mt-1">
                              <Badge variant="outline" className="text-xs">{DYNAMIC_FIELD_TYPES.find(t => t.id === field.tipo)?.name || field.tipo}</Badge>
                              {field.requerido && <Badge className="bg-red-100 text-red-700 text-xs">Requerido</Badge>}
                              {field.filtrable && <Badge className="bg-blue-100 text-blue-700 text-xs">Filtrable</Badge>}
                              {!field.activo && <Badge variant="secondary" className="text-xs">Inactivo</Badge>}
                            </div>
                          </div>
                          <Button variant="ghost" size="icon" className="text-red-600" onClick={() => handleDeleteDynamicField(field.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {dynamicFields.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No hay campos dinámicos configurados</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── HOMEPAGE TAB ─── */}
        <TabsContent value="homepage">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Home className="w-5 h-5" /> Configuración de Homepage Pública</CardTitle>
              <CardDescription>Control total sobre todo el contenido de la página de inicio pública</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="general-hp">
                <TabsList className="grid grid-cols-5 w-full mb-6">
                  <TabsTrigger value="general-hp">General</TabsTrigger>
                  <TabsTrigger value="quienes-hp">Quiénes Somos</TabsTrigger>
                  <TabsTrigger value="danzas-hp">Qué Hacemos</TabsTrigger>
                  <TabsTrigger value="porque-hp">Por Qué</TabsTrigger>
                  <TabsTrigger value="contacto-hp">Contacto</TabsTrigger>
                </TabsList>

                {/* ── GENERAL ── */}
                <TabsContent value="general-hp">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>URL del Logo (se muestra en navbar, footer, splash y sección Quiénes Somos)</Label>
                      <Input value={homepageSettings.logo_url || ''} onChange={e => setHomepageSettings({...homepageSettings, logo_url: e.target.value})} placeholder="https://example.com/logo.png" data-testid="hp-logo-url" />
                      {homepageSettings.logo_url && <img src={homepageSettings.logo_url} alt="Preview" className="h-16 mt-2 object-contain rounded border p-1" />}
                    </div>
                    <div className="space-y-2">
                      <Label>Subtítulo Hero</Label>
                      <Input value={homepageSettings.subtitulo || ''} onChange={e => setHomepageSettings({...homepageSettings, subtitulo: e.target.value})} placeholder="Fraternidad de Folklore Boliviano — Beni" data-testid="hp-subtitulo" />
                    </div>
                    <div className="space-y-2">
                      <Label>Descripción principal (aparece en el Hero)</Label>
                      <Textarea value={homepageSettings.descripcion || ''} onChange={e => setHomepageSettings({...homepageSettings, descripcion: e.target.value})} rows={2} placeholder="Llevamos la riqueza cultural..." data-testid="hp-descripcion" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Año de fundación</Label>
                        <Input type="number" value={homepageSettings.anio_fundacion || 2020} onChange={e => setHomepageSettings({...homepageSettings, anio_fundacion: parseInt(e.target.value) || 2020})} data-testid="hp-anio" />
                      </div>
                      <div className="space-y-2">
                        <Label>Tagline del footer</Label>
                        <Input value={homepageSettings.tagline_footer || ''} onChange={e => setHomepageSettings({...homepageSettings, tagline_footer: e.target.value})} data-testid="hp-tagline" />
                      </div>
                    </div>
                    <Separator />
                    <h4 className="text-sm font-medium text-stone-500 uppercase tracking-wider">Estadísticas públicas</h4>
                    <div className="grid grid-cols-4 gap-4">
                      {['anios', 'integrantes', 'actuaciones', 'paises'].map(k => (
                        <div key={k} className="space-y-2">
                          <Label className="capitalize">{k}</Label>
                          <Input type="number" value={homepageSettings.stats?.[k] || 0} onChange={e => setHomepageSettings({...homepageSettings, stats: {...(homepageSettings.stats || {}), [k]: parseInt(e.target.value) || 0}})} data-testid={`hp-stat-${k}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                {/* ── QUIÉNES SOMOS ── */}
                <TabsContent value="quienes-hp">
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">Edita los dos párrafos que aparecen en la sección "Quiénes Somos" de la homepage.</p>
                    <div className="space-y-2">
                      <Label>Párrafo 1 (historia y significado del nombre)</Label>
                      <Textarea value={homepageSettings.quienes_somos?.parrafo1 || ''} onChange={e => setHomepageSettings({...homepageSettings, quienes_somos: {...(homepageSettings.quienes_somos || {}), parrafo1: e.target.value}})} rows={4} placeholder="Somos la Fraternidad Pichiroces Amazónicos..." data-testid="hp-quienes-p1" />
                    </div>
                    <div className="space-y-2">
                      <Label>Párrafo 2 (misión y visión)</Label>
                      <Textarea value={homepageSettings.quienes_somos?.parrafo2 || ''} onChange={e => setHomepageSettings({...homepageSettings, quienes_somos: {...(homepageSettings.quienes_somos || {}), parrafo2: e.target.value}})} rows={4} placeholder="Fundada por benianos orgullosos..." data-testid="hp-quienes-p2" />
                    </div>
                  </div>
                </TabsContent>

                {/* ── QUÉ HACEMOS (DANZAS/ACTIVIDADES CRUD) ── */}
                <TabsContent value="danzas-hp">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">Gestiona las danzas y actividades que se muestran en la homepage. Se muestran 4 visibles con slider si hay más.</p>
                      <Button size="sm" onClick={() => {
                        const d = homepageSettings.danzas || [];
                        setHomepageSettings({...homepageSettings, danzas: [...d, {id: Date.now().toString(), nombre: '', descripcion: '', imagen_url: '', orden: d.length + 1}]});
                      }} data-testid="hp-add-danza"><Plus className="w-4 h-4 mr-1" />Añadir</Button>
                    </div>
                    {(homepageSettings.danzas || []).map((d, idx) => (
                      <div key={d.id || idx} className="border rounded-lg p-4 space-y-3" data-testid={`hp-danza-item-${idx}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-stone-500">#{idx + 1}</span>
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => {
                            const arr = [...(homepageSettings.danzas || [])];
                            arr.splice(idx, 1);
                            setHomepageSettings({...homepageSettings, danzas: arr});
                          }} data-testid={`hp-delete-danza-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Nombre</Label>
                            <Input value={d.nombre} onChange={e => {
                              const arr = [...(homepageSettings.danzas || [])];
                              arr[idx] = {...arr[idx], nombre: e.target.value};
                              setHomepageSettings({...homepageSettings, danzas: arr});
                            }} placeholder="Ej: Macheteros" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">URL de Imagen</Label>
                            <Input value={d.imagen_url || ''} onChange={e => {
                              const arr = [...(homepageSettings.danzas || [])];
                              arr[idx] = {...arr[idx], imagen_url: e.target.value};
                              setHomepageSettings({...homepageSettings, danzas: arr});
                            }} placeholder="https://..." />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Descripción</Label>
                          <Textarea value={d.descripcion} onChange={e => {
                            const arr = [...(homepageSettings.danzas || [])];
                            arr[idx] = {...arr[idx], descripcion: e.target.value};
                            setHomepageSettings({...homepageSettings, danzas: arr});
                          }} rows={2} />
                        </div>
                      </div>
                    ))}
                    {(!homepageSettings.danzas || homepageSettings.danzas.length === 0) && (
                      <p className="text-center text-muted-foreground py-8">No hay danzas/actividades. Haz clic en "Añadir" para crear la primera.</p>
                    )}
                  </div>
                </TabsContent>

                {/* ── POR QUÉ LO HACEMOS ── */}
                <TabsContent value="porque-hp">
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">Edita la sección "Por Qué Lo Hacemos" con título, texto, pilares y cita.</p>
                    <div className="space-y-2">
                      <Label>Título principal</Label>
                      <Textarea value={homepageSettings.seccion_por_que?.titulo || ''} onChange={e => setHomepageSettings({...homepageSettings, seccion_por_que: {...(homepageSettings.seccion_por_que || {}), titulo: e.target.value}})} rows={2} data-testid="hp-porque-titulo" />
                    </div>
                    <div className="space-y-2">
                      <Label>Párrafo descriptivo</Label>
                      <Textarea value={homepageSettings.seccion_por_que?.parrafo || ''} onChange={e => setHomepageSettings({...homepageSettings, seccion_por_que: {...(homepageSettings.seccion_por_que || {}), parrafo: e.target.value}})} rows={3} data-testid="hp-porque-parrafo" />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <Label>Pilares (3 columnas)</Label>
                      {(homepageSettings.seccion_por_que?.pilares || []).length < 4 && (
                        <Button size="sm" variant="outline" onClick={() => {
                          const sp = homepageSettings.seccion_por_que || {};
                          const pilares = [...(sp.pilares || []), {icono: 'leaf', titulo: '', descripcion: ''}];
                          setHomepageSettings({...homepageSettings, seccion_por_que: {...sp, pilares}});
                        }}><Plus className="w-4 h-4 mr-1" />Añadir Pilar</Button>
                      )}
                    </div>
                    {(homepageSettings.seccion_por_que?.pilares || []).map((p, idx) => (
                      <div key={idx} className="border rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <Select value={p.icono || 'leaf'} onValueChange={v => {
                            const sp = {...(homepageSettings.seccion_por_que || {})};
                            const pilares = [...(sp.pilares || [])];
                            pilares[idx] = {...pilares[idx], icono: v};
                            setHomepageSettings({...homepageSettings, seccion_por_que: {...sp, pilares}});
                          }}>
                            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="leaf">Hoja</SelectItem>
                              <SelectItem value="bird">Ave</SelectItem>
                              <SelectItem value="handshake">Comunidad</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => {
                            const sp = {...(homepageSettings.seccion_por_que || {})};
                            const pilares = [...(sp.pilares || [])];
                            pilares.splice(idx, 1);
                            setHomepageSettings({...homepageSettings, seccion_por_que: {...sp, pilares}});
                          }}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                        <Input value={p.titulo} onChange={e => {
                          const sp = {...(homepageSettings.seccion_por_que || {})};
                          const pilares = [...(sp.pilares || [])];
                          pilares[idx] = {...pilares[idx], titulo: e.target.value};
                          setHomepageSettings({...homepageSettings, seccion_por_que: {...sp, pilares}});
                        }} placeholder="Título del pilar" />
                        <Textarea value={p.descripcion} onChange={e => {
                          const sp = {...(homepageSettings.seccion_por_que || {})};
                          const pilares = [...(sp.pilares || [])];
                          pilares[idx] = {...pilares[idx], descripcion: e.target.value};
                          setHomepageSettings({...homepageSettings, seccion_por_que: {...sp, pilares}});
                        }} rows={2} placeholder="Descripción..." />
                      </div>
                    ))}
                    <Separator />
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Cita / Quote</Label>
                        <Textarea value={homepageSettings.seccion_por_que?.quote || ''} onChange={e => setHomepageSettings({...homepageSettings, seccion_por_que: {...(homepageSettings.seccion_por_que || {}), quote: e.target.value}})} rows={2} data-testid="hp-porque-quote" />
                      </div>
                      <div className="space-y-2">
                        <Label>Autor de la cita</Label>
                        <Input value={homepageSettings.seccion_por_que?.quote_author || ''} onChange={e => setHomepageSettings({...homepageSettings, seccion_por_que: {...(homepageSettings.seccion_por_que || {}), quote_author: e.target.value}})} data-testid="hp-porque-author" />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* ── CONTACTO & REDES ── */}
                <TabsContent value="contacto-hp">
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-stone-500 uppercase tracking-wider">Contacto</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input value={homepageSettings.contacto?.email || ''} onChange={e => setHomepageSettings({...homepageSettings, contacto: {...(homepageSettings.contacto || {}), email: e.target.value}})} placeholder="info@pichiroces.com" data-testid="hp-email" />
                      </div>
                      <div className="space-y-2">
                        <Label>WhatsApp</Label>
                        <Input value={homepageSettings.contacto?.whatsapp || ''} onChange={e => setHomepageSettings({...homepageSettings, contacto: {...(homepageSettings.contacto || {}), whatsapp: e.target.value}})} placeholder="+34 612 345 678" data-testid="hp-whatsapp" />
                      </div>
                      <div className="space-y-2">
                        <Label>Ubicación</Label>
                        <Input value={homepageSettings.contacto?.ubicacion || ''} onChange={e => setHomepageSettings({...homepageSettings, contacto: {...(homepageSettings.contacto || {}), ubicacion: e.target.value}})} data-testid="hp-ubicacion" />
                      </div>
                    </div>
                    <Separator />
                    <h4 className="text-sm font-medium text-stone-500 uppercase tracking-wider">Redes Sociales</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {['instagram', 'facebook', 'tiktok', 'youtube'].map(r => (
                        <div key={r} className="space-y-2">
                          <Label className="capitalize">{r} URL</Label>
                          <Input value={homepageSettings.redes_sociales?.[r] || ''} onChange={e => setHomepageSettings({...homepageSettings, redes_sociales: {...(homepageSettings.redes_sociales || {}), [r]: e.target.value}})} placeholder={`https://${r}.com/...`} data-testid={`hp-${r}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex justify-end mt-6 pt-4 border-t">
                <Button onClick={async () => {
                  setSaving(true);
                  try {
                    await updateHomepageSettings(homepageSettings);
                    toast.success('Homepage actualizada — los cambios ya son visibles');
                  } catch {
                    toast.error('Error al actualizar homepage');
                  } finally { setSaving(false); }
                }} disabled={saving} data-testid="hp-save-btn">
                  <Save className="w-4 h-4 mr-2" />{saving ? 'Guardando...' : 'Guardar y Publicar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Role Dialog with Granular Permissions Matrix */}
      <Dialog open={showCreateRole || !!editRole} onOpenChange={() => { setShowCreateRole(false); setEditRole(null); setRoleForm({ nombre: '', descripcion: '', es_directiva: false, nivel_acceso: 1, permisos: {}, hereda_de: '' }); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editRole ? `Editar Rol: ${editRole.nombre}` : 'Nuevo Rol'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input value={roleForm.nombre} onChange={(e) => setRoleForm({ ...roleForm, nombre: e.target.value })} data-testid="role-name" />
              </div>
              <div className="space-y-2">
                <Label>Nivel de Acceso (1-10)</Label>
                <Input type="number" min={1} max={10} value={roleForm.nivel_acceso} onChange={(e) => setRoleForm({ ...roleForm, nivel_acceso: parseInt(e.target.value) || 1 })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input value={roleForm.descripcion} onChange={(e) => setRoleForm({ ...roleForm, descripcion: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Switch checked={roleForm.es_directiva} onCheckedChange={(v) => setRoleForm({ ...roleForm, es_directiva: v })} />
                <Label>Es Directiva</Label>
              </div>
              <div className="space-y-2">
                <Label>Hereda de</Label>
                <Select value={roleForm.hereda_de || 'none'} onValueChange={(v) => setRoleForm({ ...roleForm, hereda_de: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Sin herencia" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin herencia</SelectItem>
                    {roles.filter(r => r.id !== editRole?.id).map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Permissions Matrix */}
            <div className="space-y-2">
              <Label className="text-base font-medium">Matriz de Permisos</Label>
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-stone-50 border-b">
                      <th className="text-left p-3 font-medium text-stone-600 w-40">Módulo</th>
                      <th className="p-3 font-medium text-stone-600 text-center">Ver</th>
                      <th className="p-3 font-medium text-stone-600 text-center">Crear</th>
                      <th className="p-3 font-medium text-stone-600 text-center">Editar</th>
                      <th className="p-3 font-medium text-stone-600 text-center">Eliminar</th>
                      <th className="p-3 font-medium text-stone-600 text-center">Exportar</th>
                      <th className="p-3 font-medium text-stone-600 text-center">Todo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map(mod => (
                      <tr key={mod.id} className="border-b hover:bg-stone-50">
                        <td className="p-3 font-medium text-stone-700">{mod.nombre}</td>
                        {['ver', 'crear', 'editar', 'eliminar', 'exportar'].map(accion => (
                          <td key={accion} className="p-3 text-center">
                            {mod.acciones.includes(accion) ? (
                              <Checkbox
                                checked={!!roleForm.permisos?.[mod.id]?.[accion]}
                                onCheckedChange={() => toggleModulePermission(mod.id, accion)}
                                data-testid={`perm-${mod.id}-${accion}`}
                              />
                            ) : (
                              <span className="text-stone-300">-</span>
                            )}
                          </td>
                        ))}
                        <td className="p-3 text-center">
                          <Checkbox
                            checked={mod.acciones.every(a => roleForm.permisos?.[mod.id]?.[a])}
                            onCheckedChange={() => toggleAllModulePerms(mod.id, mod.acciones)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateRole(false); setEditRole(null); }}>Cancelar</Button>
            <Button onClick={editRole ? handleUpdateRole : handleCreateRole} data-testid="submit-role-btn">{editRole ? 'Actualizar' : 'Crear'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Recurring Event Dialog */}
      <Dialog open={showCreateRecurring} onOpenChange={setShowCreateRecurring}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuevo Evento Periódico</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input value={recurringForm.title} onChange={(e) => setRecurringForm({ ...recurringForm, title: e.target.value })} data-testid="recurring-title" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={recurringForm.event_type} onValueChange={(value) => setRecurringForm({ ...recurringForm, event_type: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {eventTypes.map((t) => (
                      <SelectItem key={t.id || t.name} value={t.id || t.name.toLowerCase()}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Día de la Semana</Label>
                <Select value={String(recurringForm.day_of_week)} onValueChange={(value) => setRecurringForm({ ...recurringForm, day_of_week: parseInt(value) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(systemSettings.days_of_week || []).map((day) => (
                      <SelectItem key={day.id} value={String(day.id)}>{day.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hora Inicio</Label>
                <Input type="time" value={recurringForm.start_time} onChange={(e) => setRecurringForm({ ...recurringForm, start_time: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Hora Fin</Label>
                <Input type="time" value={recurringForm.end_time} onChange={(e) => setRecurringForm({ ...recurringForm, end_time: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Ubicación</Label>
              <Input value={recurringForm.location} onChange={(e) => setRecurringForm({ ...recurringForm, location: e.target.value })} />
            </div>
            <div className="flex items-center space-x-2">
              <Switch checked={recurringForm.is_active} onCheckedChange={(checked) => setRecurringForm({ ...recurringForm, is_active: checked })} />
              <Label>Activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateRecurring(false)}>Cancelar</Button>
            <Button onClick={handleCreateRecurring} data-testid="submit-recurring-btn">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Event Type Dialog */}
      <Dialog open={showCreateEventType} onOpenChange={setShowCreateEventType}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo Tipo de Evento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={eventTypeForm.name} onChange={(e) => setEventTypeForm({ ...eventTypeForm, name: e.target.value })} data-testid="event-type-name" />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex gap-2">
                <Input type="color" value={eventTypeForm.color} onChange={(e) => setEventTypeForm({ ...eventTypeForm, color: e.target.value })} className="w-14 h-10 p-1 cursor-pointer" />
                <Input value={eventTypeForm.color} onChange={(e) => setEventTypeForm({ ...eventTypeForm, color: e.target.value })} className="flex-1" />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox checked={eventTypeForm.requires_attendance} onCheckedChange={(checked) => setEventTypeForm({ ...eventTypeForm, requires_attendance: checked })} />
              <Label>Requiere control de asistencia</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateEventType(false)}>Cancelar</Button>
            <Button onClick={handleCreateEventType} data-testid="submit-event-type-btn">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={!!showEditCategory} onOpenChange={() => setShowEditCategory(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{showEditCategory === 'new' ? 'Nueva Categoría' : 'Editar Categoría'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={editCategoryData.name}
                onChange={(e) => setEditCategoryData({...editCategoryData, name: e.target.value})}
              />
            </div>
            {editCategoryType !== 'budget_categories' && editCategoryType !== 'inventory_categories' && (
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={editCategoryData.color || '#0284c7'}
                    onChange={(e) => setEditCategoryData({...editCategoryData, color: e.target.value})}
                    className="w-14 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    value={editCategoryData.color || ''}
                    onChange={(e) => setEditCategoryData({...editCategoryData, color: e.target.value})}
                    className="flex-1"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditCategory(null)}>Cancelar</Button>
            <Button onClick={handleSaveCategory}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dynamic Field Dialog */}
      <Dialog open={showCreateDynamicField} onOpenChange={setShowCreateDynamicField}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Campo Dinámico</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Módulo *</Label>
              <Select value={dynamicFieldForm.modulo} onValueChange={(v) => setDynamicFieldForm({...dynamicFieldForm, modulo: v})}>
                <SelectTrigger data-testid="df-modulo"><SelectValue placeholder="Selecciona módulo" /></SelectTrigger>
                <SelectContent>
                  {modules.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre interno *</Label>
                <Input value={dynamicFieldForm.nombre_campo}
                  onChange={(e) => setDynamicFieldForm({...dynamicFieldForm, nombre_campo: e.target.value.replace(/\s/g, '_').toLowerCase()})}
                  placeholder="ej: talla_traje" data-testid="df-nombre" />
              </div>
              <div className="space-y-2">
                <Label>Etiqueta visible *</Label>
                <Input value={dynamicFieldForm.etiqueta}
                  onChange={(e) => setDynamicFieldForm({...dynamicFieldForm, etiqueta: e.target.value})}
                  placeholder="ej: Talla del Traje" data-testid="df-etiqueta" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tipo de campo</Label>
              <Select value={dynamicFieldForm.tipo} onValueChange={(v) => setDynamicFieldForm({...dynamicFieldForm, tipo: v})}>
                <SelectTrigger data-testid="df-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DYNAMIC_FIELD_TYPES.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(dynamicFieldForm.tipo === 'seleccion_unica' || dynamicFieldForm.tipo === 'seleccion_multiple') && (
              <div className="space-y-2">
                <Label>Opciones (separadas por coma)</Label>
                <Input
                  value={dynamicFieldForm.opciones.join(', ')}
                  onChange={(e) => setDynamicFieldForm({...dynamicFieldForm, opciones: e.target.value.split(',').map(o => o.trim()).filter(Boolean)})}
                  placeholder="Opción 1, Opción 2, Opción 3"
                />
              </div>
            )}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox checked={dynamicFieldForm.requerido} onCheckedChange={(v) => setDynamicFieldForm({...dynamicFieldForm, requerido: v})} />
                <Label>Requerido</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox checked={dynamicFieldForm.visible_en_listado} onCheckedChange={(v) => setDynamicFieldForm({...dynamicFieldForm, visible_en_listado: v})} />
                <Label>Visible en listado</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox checked={dynamicFieldForm.filtrable} onCheckedChange={(v) => setDynamicFieldForm({...dynamicFieldForm, filtrable: v})} />
                <Label>Filtrable</Label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Orden</Label>
              <Input type="number" value={dynamicFieldForm.orden} onChange={(e) => setDynamicFieldForm({...dynamicFieldForm, orden: parseInt(e.target.value) || 0})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDynamicField(false)}>Cancelar</Button>
            <Button onClick={handleCreateDynamicField} data-testid="submit-dynamic-field-btn">Crear Campo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
